import { ISO_3166_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { cineamoApiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { MovieDto } from '../../models/movie/v2/MovieDto.types';
import { MovieLocalizedListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ V2 Movies List ------------------------>

type GetMoviesV2QueryParameter = UrlQueryParameter & {
    releaseDateStart?: string;
    releaseDateEnd?: string;
    region?: string;
    distributorId?: string;
    isShowingNow?: boolean;
    cinemaCountryCode?: ISO_3166_1_LOCALE;
    include?: string;
    /** Exact `cineamoId` lookup — used to rehydrate a stored movie reference. */
    cineamoMovieId?: string;
    /**
     * Movies the cinema has (`true`) or has not (`false`) got a showing scheduled for from now on.
     * Answered for the cinema in `X-Cinema-Id`; sending it without that header is a 400.
     */
    hasScheduledShowings?: boolean;
    /**
     * Movies the cinema has (`true`) or has not (`false`) customized. Answered for the cinema in
     * `X-Cinema-Id`; sending it without that header is a 400.
     */
    hasCustomization?: boolean;
    /** Movies that carry (`true`) or lack (`false`) a Rentrak title. */
    hasRentrakTitle?: boolean;
    /**
     * Keep only movies with at least this many Cineamo user ratings. Applies on any sort. Pair it
     * with `sort=rating`, where a movie with a single 10/10 otherwise outranks a well-rated one
     * with many. Must be 1 or more — omit it to apply no filter.
     */
    ratingsCountMin?: number;
};

type MoviesV2Config = {
    locale?: string;
    cinemaId?: number | string;
} & RequestConfig;

/**
 * Builds the V2-specific headers (Accept-Language, X-Cinema-Id).
 */
function buildV2Headers(config?: MoviesV2Config): Record<string, string> {
    const headers: Record<string, string> = {};
    if (config?.locale) {
        headers['Accept-Language'] = config.locale;
    }
    if (config?.cinemaId) {
        headers['X-Cinema-Id'] = config.cinemaId.toString();
    }
    return headers;
}

/**
 * GET /v2/movies
 *
 * Returns a locale-aware paginated list of movies.
 * Locale is resolved via Accept-Language header.
 * Cinema context (customization overrides) via X-Cinema-Id header.
 *
 * @example
 * getMovies({ page: 1, perPage: 10 }, { locale: 'de', cinemaId: 123 })
 */
export async function getMovies(
    urlParams?: GetMoviesV2QueryParameter,
    config?: MoviesV2Config
): Promise<ApiResponse<MovieLocalizedListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieLocalizedListResponse>>(`/v2/movies${buildUrlQuery(urlParams)}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            }
        })
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ V2 Movie By ID ------------------------>

/**
 * GET /v2/movies/{movieId}
 *
 * Returns a single locale-aware movie by numeric ID.
 * Supports sparse includes via ?include= query parameter.
 *
 * @example
 * getMovieById(4149063, {
 *   locale: 'de',
 *   cinemaId: 123,
 *   include: 'videos,credits,releaseDates,productionCompanies,productionCountries'
 * })
 */
export async function getMovieById(
    movieId: MovieDto['id'],
    config?: MoviesV2Config & { include?: string }
): Promise<ApiResponse<MovieDto> | null> {
    if (!movieId) {
        return null;
    }
    const query = config?.include ? `?include=${config.include}` : '';
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/v2/movies/${movieId}${query}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            },
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ V2 Movie By Slug ------------------------>

/**
 * GET /v2/movies/{movieSlug}
 *
 * Returns a single locale-aware movie by its slug.
 *
 * @example
 * getMovieBySlug('the-dark-knight', { locale: 'de' })
 */
export async function getMovieBySlug(
    movieSlug: string,
    config?: MoviesV2Config & { include?: string }
): Promise<ApiResponse<MovieDto> | null> {
    if (!movieSlug) {
        return null;
    }
    const query = config?.include ? `?include=${config.include}` : '';
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/v2/movies/${movieSlug}${query}`, {
            ...config,
            headers: {
                ...buildV2Headers(config),
                ...config?.headers
            },
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => null);
}
